import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Search, X, Star } from "lucide-react";

const STAR_MAP = {
  "3 Star": "★★★☆☆",
  "4 Star": "★★★★☆",
  "5 Star": "★★★★★",
  "5 Star Deluxe": "★★★★★+",
};

/**
 * Searchable Hotel Category dropdown backed by /api/hotel-categories.
 * - Type to filter
 * - Keyboard: ↑/↓ navigate, Enter selects
 * - "Any Other (Custom)" reveals a text input; whatever the agent types is stored as the category
 * - Backward-safe: empty string = "no category" (hidden in PDF)
 */
export default function HotelCategorySelect({ value, onChange, testid, placeholder = "Choose category…", size = "md" }) {
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [focusIdx, setFocusIdx] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    let alive = true;
    api.get("/hotel-categories").then(({ data }) => {
      if (!alive) return;
      const names = (data || []).map((c) => c.name);
      setOptions([...names, "Any Other (Custom)"]);
    }).catch(() => setOptions(["Any Other (Custom)"]));
    return () => { alive = false; };
  }, []);

  // Close on outside click
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const filtered = useMemo(() => {
    const t = query.trim().toLowerCase();
    if (!t) return options;
    return options.filter((o) => o.toLowerCase().includes(t));
  }, [query, options]);

  const isCustom = value && !options.slice(0, -1).includes(value);
  const display = isCustom ? "Any Other (Custom)" : value || "";

  const pick = (opt) => {
    if (opt === "Any Other (Custom)") {
      // Enter custom mode — clear the value so the text input appears blank
      onChange(""); setOpen(false); setQuery("");
      setTimeout(() => document.querySelector(`[data-testid="${testid}-custom"]`)?.focus(), 30);
      return;
    }
    onChange(opt);
    setOpen(false); setQuery("");
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setFocusIdx((i) => Math.min(filtered.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setFocusIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); if (filtered[focusIdx]) pick(filtered[focusIdx]); }
    else if (e.key === "Escape") setOpen(false);
  };

  const height = size === "sm" ? "h-8 text-xs" : "h-10 text-sm";
  const showCustomText = isCustom || (display === "Any Other (Custom)");

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={testid}
        onClick={() => { setOpen((o) => !o); setFocusIdx(0); }}
        className={`w-full flex items-center gap-2 border border-border rounded px-3 ${height} bg-white text-left hover:border-emerald-500`}
      >
        <Star size={13} className="text-emerald-600 shrink-0" />
        <span className={`flex-1 truncate ${!value ? "text-slate-400" : "text-slate-800"}`}>
          {value ? (STAR_MAP[value] ? `${STAR_MAP[value]}  ${value}` : value) : placeholder}
        </span>
        {value && (
          <span onClick={(e) => { e.stopPropagation(); onChange(""); }} className="text-slate-400 hover:text-red-500" title="Clear">
            <X size={13} />
          </span>
        )}
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-border rounded shadow-lg max-h-64 overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
            <Search size={12} className="text-slate-400" />
            <input
              autoFocus
              data-testid={testid ? `${testid}-search` : undefined}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setFocusIdx(0); }}
              onKeyDown={onKey}
              placeholder="Type to filter…"
              className="flex-1 text-sm outline-none"
            />
          </div>
          <div className="max-h-52 overflow-y-auto">
            {filtered.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No matches.</div>}
            {filtered.map((opt, i) => (
              <button
                key={opt}
                type="button"
                data-testid={testid ? `${testid}-opt-${opt.replace(/\s+/g, "-").toLowerCase()}` : undefined}
                onClick={() => pick(opt)}
                onMouseEnter={() => setFocusIdx(i)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${i === focusIdx ? "bg-emerald-50" : ""}`}
              >
                {STAR_MAP[opt] && <span className="text-amber-500 text-xs w-14">{STAR_MAP[opt]}</span>}
                <span>{opt}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {showCustomText && (
        <input
          data-testid={testid ? `${testid}-custom` : undefined}
          value={isCustom ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Type your custom category…"
          className="mt-2 w-full border border-border rounded px-3 py-2 text-sm"
        />
      )}
    </div>
  );
}
